import { useToast } from '@react/shared/components/Toast';
import { delay } from '@react/shared/helpers/delay';
import { MutableRefObject, useState } from 'react';
import { isBrowser } from 'src/config';

type ModalNames = 'embed' | 'share' | '';

const useGenerateChart = ({
  chartContainer,
  snapshotContainer,
}: {
  chartContainer: MutableRefObject<HTMLDivElement | null>;
  snapshotContainer: MutableRefObject<HTMLDivElement | null>;
}) => {
  const [chartUrl, setChartUrl] = useState('');
  const [isModalOpen, setIsModalOpen] = useState<ModalNames>('');
  const [isLoading, setIsLoading] = useState<ModalNames>('');
  const { pushSuccess: notifyInfo, pushError: notifyError } = useToast();

  const isEmbedModalOpen = isModalOpen === 'embed';
  const isShareModalOpen = isModalOpen === 'share';
  const isLoadingEmbed = isLoading === 'embed';
  const isLoadingShare = isLoading === 'share';

  const currentUrl = isBrowser ? window.location.href : '';

  const closeModal = () => {
    setIsModalOpen('');
  };

  const handleGenerateChart = async (modalName: ModalNames) => {
    if (!modalName) return;

    try {
      setIsLoading(modalName);
      if (chartContainer.current) {
        const html2canvas = (await import('html2canvas')).default;
        if (snapshotContainer.current) {
          snapshotContainer.current.style.display = 'block';
        }
        const canvas = await html2canvas(chartContainer.current);
        const dataUrl = canvas.toDataURL();
        await delay(500);
        setChartUrl(dataUrl);
        if (snapshotContainer.current) {
          snapshotContainer.current.style.display = 'none';
        }
        setIsModalOpen(modalName);
      }
      setIsLoading('');
    } catch (error) {
      notifyError('Opps! Something went wrong. Please try again.');
      console.error('Error generating chart:', error);
      setIsLoading('');
      closeModal();
    }
  };

  const handleEmbedHtml = () => {
    navigator.clipboard.writeText(
      `<a href="${currentUrl}"><img src="${chartUrl}" alt="Generated Chart" /></a>`
    );
    notifyInfo('Copied to clipboard');
  };

  const handleShare = () => {
    navigator.clipboard.writeText(currentUrl);
    notifyInfo('Copied to clipboard');
  };

  return {
    chartUrl,
    isModalOpen,
    isLoading,
    closeModal,
    handleGenerateChart,
    isEmbedModalOpen,
    isShareModalOpen,
    isLoadingEmbed,
    isLoadingShare,
    currentUrl,
    handleEmbedHtml,
    handleShare,
  };
};

export default useGenerateChart;
